// audio/AudioMixer.ts
// Mixer wrapper that supports two data paths:
//  - SAB mode (SharedArrayBuffer ring per track) when crossOriginIsolated
//  - Message mode (postMessage of planar chunks) otherwise

export type MixerTrackHandle = { id: string };

export type MixerEvent = {
    clipHandleId: string;  // set after registerTrack()
    busId?: string;        // optional routing tag; defaults to 'master'
    start: number;         // comp-time [s]
    end: number;           // comp-time [s]
    srcOffset: number;     // source-time [s] when compTime === start
    rate: number;          // playback rate
    gain: number;          // base linear gain
    duck?: { targetBus: string; amountDb: number; attackMs?: number; releaseMs?: number } | null;
};

export type MixerSchedule = { events: MixerEvent[] };

type SABTrackState = {
    channels: number;
    sampleRate: number;
    capacityFrames: number;
    // shared
    dataSAB: SharedArrayBuffer;   // interleaved Float32 ring: frames * channels
    ctrlSAB: SharedArrayBuffer;   // Int32 control: [writeFrame, originFrame, capacityFrames, channels, sampleRate]
    metaSAB: SharedArrayBuffer;   // Float64 meta: [originTimeSec]
    dataF32: Float32Array;
    ctrlI32: Int32Array;
    metaF64: Float64Array;
    // local book-keeping for write path
    wroteAny: boolean;
};

const isSABAvailable = false;
    // typeof SharedArrayBuffer !== 'undefined' &&
    // (self as any).crossOriginIsolated === true;

const isSharedArrayBufferSupported = typeof SharedArrayBuffer !== 'undefined';
const isCOI = (globalThis as any).crossOriginIsolated === true;
console.log("AudioMixer SAB support:", {
    isSharedArrayBufferSupported,
    isCOI,
    isSABAvailable,
});

export class AudioMixer {
    private constructor(
        private ctx: AudioContext,
        private node: AudioWorkletNode,
        private useSAB: boolean,
    ) {}

    // Message-mode ordering guard: bump this on every flush to ignore stale pcm-chunk messages
    // that were posted before the flush (or by a previous feeder).
    private msgEpochByHandleId: Map<string, number> = new Map();

    static async init(ctx: AudioContext, workletUrl: string, opts?: { output?: AudioNode }) {
        await ctx.audioWorklet.addModule(workletUrl);
        const node = new AudioWorkletNode(ctx, 'libretto-mixer', {
            numberOfInputs: 0,
            numberOfOutputs: 1,
            outputChannelCount: [2],
        });
        node.connect(opts?.output ?? ctx.destination);
        const useSAB = isSABAvailable;
        // tell the processor which mode we're in
        node.port.postMessage({ type: 'mode', useSAB });
        return new AudioMixer(ctx, node, useSAB);
    }

    // ---- common API ----

    flushTrack(handle: MixerTrackHandle, originSec?: number) {
        const handleId = handle.id;
        if (!this.useSAB) {
            const nextEpoch = (this.msgEpochByHandleId.get(handleId) ?? 0) + 1;
            this.msgEpochByHandleId.set(handleId, nextEpoch);
            this.node.port.postMessage({ type: 'flush-track', handleId, originSec, epoch: nextEpoch });
            return;
        }
        this.node.port.postMessage({ type: 'flush-track', handleId, originSec });
    }

    flushAll(tracks: MixerTrackHandle[], originSecById?: Record<string, number>) {
        if (!this.useSAB) {
            const items = tracks.map((h) => {
                const handleId = h.id;
                const nextEpoch = (this.msgEpochByHandleId.get(handleId) ?? 0) + 1;
                this.msgEpochByHandleId.set(handleId, nextEpoch);
                return { handleId, originSec: originSecById?.[h.id], epoch: nextEpoch };
            });
            this.node.port.postMessage({ type: 'flush-all', items });
            return;
        }
        this.node.port.postMessage({
            type: 'flush-all',
            items: tracks.map(h => ({ handleId: h.id, originSec: originSecById?.[h.id] }))
        });
    }

    registerTrack(args: { clipId: string; trackId?: string; channels: number; sampleRate: number }): MixerTrackHandle {
        const id = `${args.clipId}:${crypto.randomUUID?.() ?? Date.now()}`;
        if (!this.useSAB) {
            this.msgEpochByHandleId.set(id, 0);
        }

        if (this.useSAB) {
            const sabState = this.createSABForTrack(args.channels, args.sampleRate);

            // Send SAB handles to the worklet (NO transfer list!)
            this.node.port.postMessage({
                type: 'register-track-sab',
                track: {
                    id,
                    channels: args.channels,
                    sampleRate: args.sampleRate,
                    dataSAB: sabState.dataSAB,
                    ctrlSAB: sabState.ctrlSAB,
                    metaSAB: sabState.metaSAB,
                },
            });

            (this as any)._sabTracks ??= new Map<string, SABTrackState>();
            (this as any)._sabTracks.set(id, sabState);
        } else {
            this.node.port.postMessage({ type: 'register-track', track: { id, ...args } });
        }

        return { id };
    }

    setSchedule(schedule: MixerSchedule) {
        this.node.port.postMessage({ type: 'schedule', schedule });
    }

    setTransport(t: { state: 'play'|'pause'|'seek'; compTime: number; rate: number }) {
        this.node.port.postMessage({ type: 'transport', ...t });
    }

    setClipGain(handle: MixerTrackHandle, gain: number) {
        this.node.port.postMessage({ type: 'clip-gain', handleId: handle.id, gain });
    }

    /** Send decoded PCM chunk (planar). ArrayBuffers are transferred, not copied in message-mode.
     *  In SAB-mode we interleave + copy into the shared ring and bump the write index.
     */
    writePcm(handle: MixerTrackHandle, chunk: {
        timestamp: number; frames: number; channels: number; planes: Float32Array[];
    }) {
        if (this.useSAB) {
            this.writePcmSAB(handle, chunk);
            return;
        }
        const epoch = this.msgEpochByHandleId.get(handle.id) ?? 0;
        const payload = {
            type: 'pcm-chunk',
            handleId: handle.id,
            epoch,
            timestamp: chunk.timestamp,
            frames: chunk.frames,
            channels: chunk.channels,
            planes: chunk.planes.map(p => p.buffer),
        };
        this.node.port.postMessage(payload, chunk.planes.map(p => p.buffer));
    }

    // ---- SAB path internals ----

    private createSABForTrack(channels: number, sampleRate: number): SABTrackState {
        // Choose a ring capacity (frames). ~1s is generous; adjust as needed.
        const capacityFrames = Math.max(1, sampleRate); // ~1 second ring
        const dataSAB = new SharedArrayBuffer(capacityFrames * channels * 4); // Float32 interleaved
        const ctrlSAB = new SharedArrayBuffer(5 * 4); // Int32: [writeFrame, originFrame, capacity, channels, sampleRate]
        const metaSAB = new SharedArrayBuffer(1 * 8); // Float64: [originTimeSec]

        const dataF32 = new Float32Array(dataSAB);
        const ctrlI32 = new Int32Array(ctrlSAB);
        const metaF64 = new Float64Array(metaSAB);

        ctrlI32[0] = 0;                    // writeFrame
        ctrlI32[1] = 0;                    // originFrame
        ctrlI32[2] = capacityFrames;
        ctrlI32[3] = channels;
        ctrlI32[4] = sampleRate;
        metaF64[0] = Number.NaN;           // originTimeSec (unset)

        return {
            channels, sampleRate, capacityFrames,
            dataSAB, ctrlSAB, metaSAB, dataF32, ctrlI32, metaF64,
            wroteAny: false,
        };
    }

    private writePcmSAB(handle: MixerTrackHandle, chunk: {
        timestamp: number; frames: number; channels: number; planes: Float32Array[];
    }) {
        const tracks: Map<string, SABTrackState> = (this as any)._sabTracks;
        const st = tracks.get(handle.id);
        if (!st) return;

        const { dataF32, ctrlI32, metaF64, channels, capacityFrames, sampleRate } = st;
        // Initialize origin on first write
        if (!st.wroteAny || Number.isNaN(metaF64[0])) {
            const originFrame = Math.floor(chunk.timestamp * sampleRate);
            Atomics.store(ctrlI32, 1, originFrame);     // originFrame
            metaF64[0] = chunk.timestamp;               // originTimeSec
            Atomics.store(ctrlI32, 0, 0);               // writeFrame
            st.wroteAny = true;
        }

        // Expect contiguous or forward timestamps; if we detect a jump back, reset origin
        const expectedNextFrame = Atomics.load(ctrlI32, 1) + Atomics.load(ctrlI32, 0);
        const thisStartFrame = Math.floor(chunk.timestamp * sampleRate);
        if (thisStartFrame !== expectedNextFrame) {
            // reset ring to this chunk
            Atomics.store(ctrlI32, 1, thisStartFrame); // originFrame
            metaF64[0] = chunk.timestamp;
            Atomics.store(ctrlI32, 0, 0);              // writeFrame
        }

        // Interleave planes into the ring at writeFrame
        const writeFrame0 = Atomics.load(ctrlI32, 0);
        let w = writeFrame0;
        const C = channels;
        const wrapMask = capacityFrames; // not a mask; use modulo

        // temp views
        const chViews = chunk.planes;

        let remaining = chunk.frames;
        let srcOffset = 0;
        while (remaining > 0) {
            const spaceToEnd = capacityFrames - (w % capacityFrames);
            const run = Math.min(remaining, spaceToEnd);
            // write 'run' frames interleaved
            for (let i = 0; i < run; i++) {
                const dstBase = ((w % capacityFrames) + i) * C;
                for (let c = 0; c < C; c++) {
                    dataF32[dstBase + c] = chViews[c][srcOffset + i] || 0;
                }
            }
            w += run;
            srcOffset += run;
            remaining -= run;
        }

        Atomics.store(ctrlI32, 0, w % capacityFrames); // writeFrame relative to origin (mod capacity)

        // Tell worklet that new audio is available (lightweight ping)
        this.node.port.postMessage({ type: 'pcm-commit', handleId: handle.id, endFrame: thisStartFrame + chunk.frames });
    }
}
